import React, { useState, KeyboardEvent, ClipboardEvent } from 'react'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'

import { isValidEmail } from '../../voucherEmailUtils'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minHeight: 48,
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
    },
    '&:focus-within': {
      borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
    },
  },
  rootDisabled: {
    opacity: 0.6,
    pointerEvents: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.26)',
    '&:hover': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.26)',
    },
    '&:focus-within': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.26)',
    },
  },
  input: {
    flex: 1,
    minWidth: 160,
    border: 'none',
    outline: 'none',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 16,
    lineHeight: '24px',
    padding: theme.spacing(0.5, 0),
    background: 'transparent',
  },
  label: {
    fontFamily: 'Montserrat',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '16px',
    color: 'rgba(0, 0, 0, 0.54)',
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  errorText: {
    fontFamily: 'Montserrat',
    fontSize: 12,
    lineHeight: '16px',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  chip: {
    fontFamily: 'Montserrat, sans-serif',
    maxWidth: '100%',
  },
}))

const COMMIT_KEYS = new Set(['Enter', ',', ' '])
const EMAIL_SPLIT_PATTERN = /[\n,;\s]+/

interface EmailChipsInputProps {
  emails: string[]
  onChange: (emails: string[]) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  onInvalidEmail?: (email: string) => void
}

const normalizeToken = (token: string): string => token.trim().toLowerCase()

const parseTokens = (text: string): string[] =>
  text
    .split(EMAIL_SPLIT_PATTERN)
    .map(normalizeToken)
    .filter(token => token.length > 0)

const EmailChipsInput: React.FC<EmailChipsInputProps> = ({
  emails,
  onChange,
  label,
  placeholder,
  disabled,
  onInvalidEmail,
}) => {
  const classes = useStyles()
  const [inputValue, setInputValue] = useState('')
  const [inputError, setInputError] = useState('')

  const addEmails = (tokens: string[]) => {
    if (!tokens.length) return

    const nextEmails = [...emails]
    const invalidEmails: string[] = []

    tokens.forEach(token => {
      if (!isValidEmail(token)) {
        invalidEmails.push(token)
        return
      }
      if (!nextEmails.includes(token)) {
        nextEmails.push(token)
      }
    })

    if (invalidEmails.length) {
      const message = `Email inválido: ${invalidEmails.join(', ')}`
      setInputError(message)
      invalidEmails.forEach(email => onInvalidEmail?.(email))
      return
    }

    setInputError('')
    onChange(nextEmails)
  }

  const commitInput = (rawValue = inputValue) => {
    const tokens = parseTokens(rawValue)
    if (!tokens.length) return
    addEmails(tokens)
    setInputValue('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!COMMIT_KEYS.has(event.key)) return
    event.preventDefault()
    commitInput()
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text')
    if (!pastedText.trim()) return

    event.preventDefault()
    const tokens = parseTokens(`${inputValue}${pastedText}`)
    addEmails(tokens)
    setInputValue('')
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    onChange(emails.filter(email => email !== emailToRemove))
  }

  return (
    <div>
      {label ? <span className={classes.label}>{label}</span> : null}
      <div className={`${classes.root}${disabled ? ` ${classes.rootDisabled}` : ''}`}>
        {emails.map(email => (
          <Chip
            key={email}
            label={email}
            onDelete={disabled ? undefined : () => handleRemoveEmail(email)}
            className={classes.chip}
            size="small"
          />
        ))}
        {!disabled && (
          <input
            className={classes.input}
            value={inputValue}
            placeholder={emails.length ? '' : placeholder}
            onChange={event => {
              setInputValue(event.target.value)
              if (inputError) setInputError('')
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => commitInput()}
            onPaste={handlePaste}
            disabled={disabled}
            aria-label={label || 'Segmentar por Correo Electrónico'}
          />
        )}
      </div>
      {inputError && <span className={classes.errorText}>{inputError}</span>}
    </div>
  )
}

EmailChipsInput.defaultProps = {
  label: '',
  placeholder: 'usuario@arcor.com.ar',
  disabled: false,
  onInvalidEmail: undefined,
}

export { EmailChipsInput }
